import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authApi, setToken, getToken } from '../api/client';

const AuthContext = createContext(null);

const DEMO_ACCOUNTS = [
  { name: 'Client Démo', email: 'client@tarjemlek.tn', password: 'client123', role: 'client' },
  { name: 'Traducteur Démo', email: 'translator@tarjemlek.tn', password: 'translator123', role: 'translator' },
  { name: 'Admin Démo', email: 'admin@tarjemlek.tn', password: 'admin123', role: 'admin' },
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function restore() {
      const token = getToken();
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const data = await authApi.me();
        if (!cancelled) setUser(data.user);
      } catch {
        setToken(null);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const data = await authApi.register(payload);
      setToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: err.data?.error || err.message || 'emailExists' };
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const data = await authApi.login(email, password);
      setToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: err.data?.error || err.message || 'invalidCredentials' };
    }
  }, []);

  const setSession = useCallback((data) => {
    setToken(data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (patch) => {
    try {
      const data = await authApi.updateMe(patch);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch {
      return { ok: false };
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: Boolean(user),
        login,
        register,
        logout,
        setSession,
        updateProfile,
        demoAccounts: DEMO_ACCOUNTS,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
